import { useCallback, useMemo } from "react";
import { Dp, ItemBuild } from "./CoState";
import { Filter } from "./CoQuery";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import UnitImage from "common/src/tft-static/UnitImage";
import ItemImage from "common/src/tft-static/ItemImage";
// import { make as ItemName } from "common/src/tft-static/ItemName.bs";
import { parseSearch, searchUnitItems } from "../common/Search2";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import * as Data from "common/src/data/Data.bs";
import { make as UnitImageExtended } from "common/src/tft-static/UnitImageExtended.bs";
import { make as UnitTooltip } from "common/src/tft-static/UnitTooltip.bs";
import { make as ItemTooltip } from "common/src/tft-static/ItemTooltip.bs";
import { use as useSet } from "common/src/data/TftSet.bs";
import { ItemFilters } from "./CoItems";
import * as T from "common/src/bindings/Translation2";
import { IconButton } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";

const cols = [
  makeCol({
    width: 180,
    order: (build, _, { unitsT }) => unitsT(build.unit),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Build")} </>,
    renderValue: (build: ItemBuild, { onExpand }) => (
      <div
        className="flex gap-[6px] items-center text-sm cursor-pointer"
        onClick={(_) => {
          onExpand(build);
        }}
      >
        {build.stars === 0 ? (
          <UnitTooltip unit={build.unit}>
            <UnitImage unitId={build.unit} size={27} />
          </UnitTooltip>
        ) : (
          <UnitTooltip unit={build.unit}>
            <UnitImageExtended
              unitId={build.unit}
              size={27}
              starLevel={build.stars}
              items={[]}
            />
          </UnitTooltip>
        )}
        {build.items.map((itemId, idx) => (
          <ItemTooltip key={idx} itemId={itemId}>
            <ItemImage itemId={itemId} size={27} rounded="rounded" />
          </ItemTooltip>
        ))}
      </div>
    ),
  }),
  makeCol({
    width: 90,
    order: (build) => build.data.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: (build) => <> {humanReadableCount(build.data.count)} </>,
  }),
  makeCol({
    width: 90,
    order: (build) => build.data.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Play rate")} </>,
    tooltip: (build) => (
      <> {humanReadableCount(build.data.count) + " games"} </>
    ),
    renderValue: (build, { baseCount }) => (
      <> {formatPct2(build.data.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: (build) => build.data.place / build.data.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: (build) => (
      <> {(build.data.place / build.data.count).toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: (build) => build.data.delta,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => (
      <Tooltip
        title={
          "Delta shows the difference in average placement of the selected filter combination with the item compared to the average placement of the filter combination without the item. Lower is better."
        }
      >
        <div>{t("Delta")}</div>
      </Tooltip>
    ),
    renderValue: (build) => (
      <> {(build.data.delta > 0 ? "+" : "") + build.data.delta.toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 90,
    order: (build) => build.data.top4 / build.data.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: (build) => (
      <> {formatPct2(build.data.top4 / build.data.count)} </>
    ),
  }),
  makeCol({
    width: 80,
    order: (build) => build.data.won / build.data.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: (build) => (
      <> {formatPct2(build.data.won / build.data.count)} </>
    ),
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: (build, { onExpand, hideExpand }, { itemsT }) => {
      return hideExpand ? (
        <div />
      ) : (
        <>
          <IconButton
            onClick={(_) => onExpand(build)}
            classes={{
              root: "text-lg",
            }}
          >
            <OpenInFull fontSize="inherit" />
          </IconButton>
        </>
      );
    },
  }),
];

type Props = {
  itemFilters: ItemFilters;
  builds: Array<ItemBuild>;
  baseCount: number;
  onExpand: (f: ItemBuild) => void;
  hideExpand: boolean;
  search: string;
  minSample: number;
  hasUnitFilters: boolean;
};

const CoBuilds = ({
  builds,
  baseCount,
  onExpand,
  hideExpand,
  search,
  itemFilters,
  minSample,
  hasUnitFilters,
}: Props) => {
  const set = useSet();
  const keyFn = useCallback((build: ItemBuild) => JSON.stringify(build), []);
  const context = useMemo(
    () => ({ baseCount, onExpand, hideExpand }),
    [baseCount, onExpand, hideExpand],
  );

  const visibleFilter = useMemo(() => {
    const filtersEnabled =
      !(
        itemFilters.craftable &&
        itemFilters.ornn &&
        itemFilters.emblem &&
        itemFilters.radiant &&
        itemFilters.other &&
        itemFilters.support
      ) &&
      (itemFilters.craftable ||
        itemFilters.ornn ||
        itemFilters.emblem ||
        itemFilters.radiant ||
        itemFilters.other ||
        itemFilters.support);

    let passSearch: any;
    if (search === "") {
      passSearch = () => true;
    } else {
      const expr = parseSearch(search);
      passSearch = (unit: string, items: Array<string>) => {
        return searchUnitItems(expr, unit, items, set);
      };
    }

    const reqTyps: Array<string> = [];
    if (filtersEnabled) {
      if (itemFilters.craftable) {
        reqTyps.push("Craftable");
      }
      if (itemFilters.emblem) {
        reqTyps.push("CraftableEmblem");
        reqTyps.push("ElusiveEmblem");
      }
      if (itemFilters.ornn) {
        reqTyps.push("Artifact");
      }
      if (itemFilters.radiant) {
        reqTyps.push("Radiant");
      }
      if (itemFilters.support) {
        reqTyps.push("Support");
      }
      if (itemFilters.other) {
        reqTyps.push("Other");
      }
    }

    return (build: ItemBuild) => {
      let passTypFilter = true;
      if (filtersEnabled && reqTyps.length > 0) {
        const typs = build.items
          .map((itemId) => {
            // @ts-ignore
            const info = Data.itemInfoExn(itemId, set);
            return info.tags;
          })
          .flat();
        passTypFilter = typs.every((typ) => reqTyps.includes(typ));
      }

      let passSearchFilter = passSearch(build.unit, build.items);

      return passTypFilter && passSearchFilter;
    };
  }, [search, itemFilters]);

  const filteredBuilds = useMemo(
    () => builds.filter((build) => build.data.count >= minSample),
    [builds, minSample],
  );

  let hasResults =
    search === "" || filteredBuilds.some((v) => visibleFilter(v));

  if (filteredBuilds.length === 0) {
    if (!hasUnitFilters) {
      return (
        <div className="px-5 py-6 text-xl text-medium text-white2">
          {T.common("Select unit filters to display build stats for them")}
        </div>
      );
    } else {
      return (
        <div className="px-5 py-6 text-xl text-medium text-white2">
          No builds with at least {minSample} games played
        </div>
      );
    }
  } else {
    return (
      <>
        <StatsTable
          cols={cols}
          context={context}
          data={filteredBuilds}
          keyFn={keyFn}
          stickyColCount={1}
          defaultSortIdx={1}
          top="t262"
          visibleFilter={visibleFilter}
        />
        {hasResults ? null : (
          <div className="p-5 text-xl text-medium text-white2">
            No results found matching the search
          </div>
        )}
      </>
    );
  }
};

export default CoBuilds;
